import { TableContainer, Paper, Table, TableCell, TableHead, TableRow, TableBody, IconButton } from '@material-ui/core';
import { ExitToApp as ExitToAppIcon } from '@material-ui/icons';
import { Organization } from '../../types/organization.type';
import { ADMIN_ORGANIZATIONS_APP_PATHS } from '../../constants/organizations-paths.constants';
import OrganizationTypeChip from '../Chips/OrganizationTypeChip.component';
import { formatDateToReadable } from '../../../shared/utils/format-date.util';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';

type OrganizationsTableHeader = {
  key: string;
  label?: string;
  minWidth: number;
  align: 'left' | 'right';
};

type Props = {
  isLoading: boolean;
  maxHeight: string;
  organizations: Organization[];
};

const ORGANIZATIONS_TABLE_HEADERS: OrganizationsTableHeader[] = [
  { key: 'id', minWidth: 50, align: 'left' },
  { key: 'label', label: 'Nom', minWidth: 250, align: 'left' },
  { key: 'type', label: 'Type', minWidth: 150, align: 'left' },
  { key: 'address', label: 'Adresse', minWidth: 250, align: 'left' },
  { key: 'createdAt', label: 'Création le', minWidth: 150, align: 'right' },
];

function OrganizationsTableEmptyBody({ headers }: { headers: OrganizationsTableHeader[] }) {
  return (
    <TableBody>
      <TableRow>
        <TableCell colSpan={headers.length + 2} style={{ textAlign: 'center' }}>
          Aucune organisation
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

function OrganizationsTableHeader({ headers }: { headers: OrganizationsTableHeader[] }) {
  return (
    <TableHead>
      <TableRow>
        {headers.map((header) => (
          <TableCell key={header.key} align={header.align} style={{ minWidth: header.minWidth, fontWeight: 'bold' }}>
            {header?.label ?? ''}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function OrganizationTableRow({ organization }: { organization: Organization }) {
  const href = ADMIN_ORGANIZATIONS_APP_PATHS.ORGANIZATION_DETAILS.replace(':organizationId', organization.id);

  return (
    <TableRow key={organization.id}>
      <TableCell align="center" style={{ maxWidth: '72svh' }}>
        <IconButton size="small" aria-label="show" href={href}>
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      <TableCell>{organization.name}</TableCell>
      <TableCell align="left">
        <OrganizationTypeChip type={organization.type} />
      </TableCell>
      <TableCell align="left">
        <span style={{ display: 'block' }}>{organization.address.street}</span>
        <span>
          {organization.address.zipCode} - {organization.address.city}
        </span>
      </TableCell>
      <TableCell align="right">{formatDateToReadable(organization.createdAt)}</TableCell>
    </TableRow>
  );
}

function OrganizationTableBody({ organizations }: { organizations: Organization[] }) {
  return (
    <TableBody>
      {organizations.map((organization) => (
        <OrganizationTableRow key={organization.id} organization={organization} />
      ))}
    </TableBody>
  );
}

export default function OrganizationsTable({ maxHeight, organizations, isLoading }: Props) {
  const hasOrganizations = !!organizations.length;

  return (
    <Paper>
      <TableContainer style={{ maxHeight }}>
        <Table stickyHeader>
          <OrganizationsTableHeader headers={ORGANIZATIONS_TABLE_HEADERS} />
          {isLoading && <TableBodySkeleton rows={50} columns={ORGANIZATIONS_TABLE_HEADERS.length} />}
          {!isLoading && !hasOrganizations && <OrganizationsTableEmptyBody headers={ORGANIZATIONS_TABLE_HEADERS} />}
          {!isLoading && hasOrganizations && <OrganizationTableBody organizations={organizations} />}
        </Table>
      </TableContainer>
    </Paper>
  );
}
